import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line } from 'recharts';
import { Calendar, DollarSign } from 'lucide-react';
import { ACCENT, GREEN, BLUE, Spinner, KpiCard } from './shared';

export function RevenueTab() {
  const [period, setPeriod] = useState('month');
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(() => {
    setLoading(true);
    adminApi.get(`/admin/analytics/business?period=${period}`).then(r => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading && !d) return <Spinner />;

  const periodLabel = period === 'day' ? 'Oggi' : period === 'week' ? 'Questa settimana' : 'Questo mese';
  const prevLabel = period === 'day' ? 'Ieri' : period === 'week' ? 'Settimana scorsa' : 'Mese scorso';

  return (
    <div className="space-y-5" data-testid="revenue-tab">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {[{v:'day',l:'Giorno'},{v:'week',l:'Settimana'},{v:'month',l:'Mese'}].map(p => (
          <button key={p.v} onClick={() => setPeriod(p.v)} data-testid={`period-${p.v}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${period === p.v ? 'bg-white text-[#D44E20] shadow-sm' : 'text-[#64748B]'}`}>
            {p.l}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label={`Visite ${periodLabel.toLowerCase()}`} value={d?.current_period?.visits || 0} change={d?.visits_change_pct} icon={Calendar} color={ACCENT} testid="rev-visits-current" />
        <KpiCard label={`Visite ${prevLabel.toLowerCase()}`} value={d?.prev_period?.visits || 0} icon={Calendar} color="#888" testid="rev-visits-prev" />
        <KpiCard label={`Ricavi ${periodLabel.toLowerCase()}`} value={`\u20AC${(d?.current_period?.revenue || 0).toLocaleString('it-IT')}`} change={d?.revenue_change_pct} icon={DollarSign} color={GREEN} testid="rev-rev-current" />
        <KpiCard label={`Ricavi ${prevLabel.toLowerCase()}`} value={`\u20AC${(d?.prev_period?.revenue || 0).toLocaleString('it-IT')}`} icon={DollarSign} color="#888" testid="rev-rev-prev" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <p className="text-[#64748B] text-xs mb-1">Media visite/giorno</p>
          <p className="text-2xl font-bold text-[#D44E20] tabular-nums">{d?.averages?.visits_per_day || 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <p className="text-[#64748B] text-xs mb-1">Media ricavi/giorno</p>
          <p className="text-2xl font-bold text-[#3ECF8E] tabular-nums">{'\u20AC'}{d?.averages?.revenue_per_day || 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <p className="text-[#64748B] text-xs mb-1">Scontrino medio</p>
          <p className="text-2xl font-bold text-[#3B82F6] tabular-nums">{'\u20AC'}{d?.averages?.avg_receipt || 0}</p>
        </div>
      </div>
      {d?.trend?.length > 0 && (
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] mb-4">Trend per {period === 'day' ? 'giorno (30gg)' : period === 'week' ? 'settimana (12 sett.)' : 'mese (12 mesi)'}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={d.trend} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#888' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#888' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#888' }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="visits" name="Visite" fill={ACCENT} radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="revenue" name="Ricavi (\u20AC)" stroke={GREEN} strokeWidth={2} dot={{ fill: GREEN, r: 3 }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
